import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  InputAdornment,
  Grid,
  Chip,
  AlertTitle,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  Save as SaveIcon,
  RestartAlt as ResetIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import {
  getBookingApprovalTimeout,
  updateBookingApprovalTimeout,
  validateBookingApprovalTimeout,
  minutesToHoursDisplay,
  DEFAULT_BOOKING_APPROVAL_TIMEOUT,
  type BookingApprovalTimeout,
  type BookingApprovalTimeoutUpdate,
  type ApiErrorResponse,
} from '@/services/settingsService';

interface SettingsState {
  paid_timeout_minutes: number;
  unpaid_timeout_minutes: number;
}

const BookingTimeoutSettingsPage: React.FC = () => {
  const [settings, setSettings] = useState<SettingsState>({
    paid_timeout_minutes: DEFAULT_BOOKING_APPROVAL_TIMEOUT.paid_timeout_minutes,
    unpaid_timeout_minutes: DEFAULT_BOOKING_APPROVAL_TIMEOUT.unpaid_timeout_minutes,
  });
  const [originalSettings, setOriginalSettings] = useState<SettingsState | null>(null);
  const [metadata, setMetadata] = useState<Omit<BookingApprovalTimeout, 'paid_timeout_minutes' | 'unpaid_timeout_minutes'> | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [rateLimitRetryAfter, setRateLimitRetryAfter] = useState<number | null>(null);
  const [showConflictDialog, setShowConflictDialog] = useState(false);

  // Calculate if there are unsaved changes
  const hasChanges = originalSettings !== null && (
    settings.paid_timeout_minutes !== originalSettings.paid_timeout_minutes ||
    settings.unpaid_timeout_minutes !== originalSettings.unpaid_timeout_minutes
  );

  // Rate limit countdown effect
  useEffect(() => {
    if (rateLimitRetryAfter === null) return;

    const interval = setInterval(() => {
      setRateLimitRetryAfter((prev) => {
        if (prev === null || prev <= 1) {
          clearInterval(interval);
          return null;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [rateLimitRetryAfter]);

  // Format countdown timer
  const formatCountdown = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const result = await getBookingApprovalTimeout();

      if (result.success && result.data) {
        const newSettings: SettingsState = {
          paid_timeout_minutes: result.data.paid_timeout_minutes,
          unpaid_timeout_minutes: result.data.unpaid_timeout_minutes,
        };
        setSettings(newSettings);
        setOriginalSettings(newSettings);

        // Store metadata (defaults, last updated info)
        setMetadata({
          defaults: result.data.defaults,
          last_updated_at: result.data.last_updated_at,
          last_updated_by: result.data.last_updated_by,
        });
      } else {
        // Handle error response
        handleApiError(result.error);
        setSettings(DEFAULT_BOOKING_APPROVAL_TIMEOUT);
        setOriginalSettings(DEFAULT_BOOKING_APPROVAL_TIMEOUT);
      }
    } catch (error) {
      console.error('Error loading booking timeout settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleApiError = (error: ApiErrorResponse | string | undefined) => {
    if (!error) {
      toast.error('An unknown error occurred');
      return;
    }

    // Handle string errors (legacy format)
    if (typeof error === 'string') {
      toast.error(error);
      return;
    }

    // Handle structured error responses
    const apiError = error as ApiErrorResponse;

    switch (apiError.code) {
      case 'UNAUTHORIZED':
        toast.error('Session expired. Please log in again.');
        // Redirect to login
        window.location.href = '/admin/login';
        break;

      case 'FORBIDDEN':
        toast.error('You do not have permission to modify these settings');
        break;

      case 'VALIDATION_ERROR':
        // Field-level errors
        if (apiError.details && typeof apiError.details === 'object') {
          setErrors(apiError.details as Record<string, string>);
          toast.error('Please fix the validation errors');
        } else {
          toast.error(apiError.message);
        }
        break;

      case 'INVALID_REQUEST':
        toast.error(apiError.message);
        break;

      case 'CONFLICT':
        setShowConflictDialog(true);
        break;

      case 'RATE_LIMIT_EXCEEDED':
        if (apiError.retry_after) {
          setRateLimitRetryAfter(apiError.retry_after);
          toast.error(`Too many requests. Try again in ${formatCountdown(apiError.retry_after)}`);
        } else {
          toast.error('Too many requests. Please try again later.');
        }
        break;

      case 'SERVER_ERROR':
      case 'SERVICE_UNAVAILABLE':
        toast.error(apiError.message || 'Server error. Please try again later.');
        break;

      default:
        toast.error(apiError.message || 'An error occurred');
    }
  };

  const handleRefreshAfterConflict = () => {
    setShowConflictDialog(false);
    loadSettings();
    toast.success('Settings refreshed');
  };

  const handleSave = async () => {
    // Client-side validation
    const validationErrors = validateBookingApprovalTimeout(
      settings.paid_timeout_minutes,
      settings.unpaid_timeout_minutes
    );

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      toast.error('Please fix the errors before saving');
      return;
    }

    // Check if at least one field changed
    if (!hasChanges) {
      toast.error('No changes to save');
      return;
    }

    // Build partial update payload
    const updates: BookingApprovalTimeoutUpdate = {};
    if (settings.paid_timeout_minutes !== originalSettings?.paid_timeout_minutes) {
      updates.paid_timeout_minutes = settings.paid_timeout_minutes;
    }
    if (settings.unpaid_timeout_minutes !== originalSettings?.unpaid_timeout_minutes) {
      updates.unpaid_timeout_minutes = settings.unpaid_timeout_minutes;
    }

    setErrors({});
    setSaving(true);

    try {
      const result = await updateBookingApprovalTimeout(updates);

      if (result.success) {
        toast.success(result.message || 'Settings saved successfully');

        // Update original settings to reflect saved state
        setOriginalSettings(settings);

        // Update metadata if provided
        if (result.data) {
          setMetadata({
            defaults: result.data.defaults,
            last_updated_at: result.data.last_updated_at,
            last_updated_by: result.data.last_updated_by,
          });
        }
      } else {
        handleApiError(result.error);
      }
    } catch (error) {
      console.error('Error saving booking timeout settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (metadata?.defaults) {
      setSettings({
        paid_timeout_minutes: metadata.defaults.paid_timeout_minutes,
        unpaid_timeout_minutes: metadata.defaults.unpaid_timeout_minutes,
      });
    } else {
      setSettings(DEFAULT_BOOKING_APPROVAL_TIMEOUT);
    }
    setErrors({});
    toast.success('Reset to default settings');
  };

  const handleFieldChange = (field: keyof SettingsState, value: string) => {
    const numValue = parseInt(value, 10);

    setSettings((prev) => ({
      ...prev,
      [field]: isNaN(numValue) ? '' as any : numValue,
    }));

    // Clear error for this field
    if (errors[field]) {
      const newErrors = { ...errors };
      delete newErrors[field];
      setErrors(newErrors);
    }
  };

  const handleFieldBlur = (field: keyof SettingsState) => {
    // Validate on blur
    const fieldErrors = validateBookingApprovalTimeout(
      field === 'paid_timeout_minutes' ? settings.paid_timeout_minutes : undefined,
      field === 'unpaid_timeout_minutes' ? settings.unpaid_timeout_minutes : undefined
    );

    if (fieldErrors[field]) {
      setErrors((prev) => ({ ...prev, [field]: fieldErrors[field] }));
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Page Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ScheduleIcon />
          Booking Approval Timeout Settings
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Configure automatic timeout periods for booking approvals
        </Typography>
      </Box>

      {/* Last Updated Info */}
      {metadata?.last_updated_at && metadata?.last_updated_by && (
        <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 3 }}>
          <AlertTitle>Last Updated</AlertTitle>
          {new Date(metadata.last_updated_at).toLocaleString()} by{' '}
          <strong>{metadata.last_updated_by.name}</strong> ({metadata.last_updated_by.email})
        </Alert>
      )}

      {/* Rate Limit Warning */}
      {rateLimitRetryAfter !== null && (
        <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 3 }}>
          <AlertTitle>Rate Limit Exceeded</AlertTitle>
          Too many requests. Please wait {formatCountdown(rateLimitRetryAfter)} before trying again.
        </Alert>
      )}

      {/* Main Settings Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3}>
            {/* Paid Bookings Timeout */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Paid Bookings
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Timeout for paid bookings awaiting host approval
              </Typography>

              <TextField
                fullWidth
                type="number"
                label="Timeout (minutes)"
                value={settings.paid_timeout_minutes}
                onChange={(e) => handleFieldChange('paid_timeout_minutes', e.target.value)}
                onBlur={() => handleFieldBlur('paid_timeout_minutes')}
                error={!!errors.paid_timeout_minutes}
                helperText={errors.paid_timeout_minutes || 'Range: 0-4320 minutes (0-72 hours)'}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <ScheduleIcon />
                    </InputAdornment>
                  ),
                  endAdornment: settings.paid_timeout_minutes !== originalSettings?.paid_timeout_minutes && (
                    <InputAdornment position="end">
                      <Chip label="Modified" size="small" color="warning" />
                    </InputAdornment>
                  ),
                }}
                inputProps={{
                  min: 0,
                  max: 4320,
                  step: 1,
                }}
              />

              <Box sx={{ mt: 1, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                {settings.paid_timeout_minutes && (
                  <Chip
                    label={`≈ ${minutesToHoursDisplay(settings.paid_timeout_minutes)}`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                )}
                {metadata?.defaults && (
                  <Typography variant="caption" color="text.secondary">
                    Default: {metadata.defaults.paid_timeout_minutes} min ({minutesToHoursDisplay(metadata.defaults.paid_timeout_minutes)})
                  </Typography>
                )}
              </Box>
            </Grid>

            {/* Unpaid Bookings Timeout */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Payment window for admin/partner bookings
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                How long a customer has to pay for a booking an admin or partner created for them. It holds the car until then.
              </Typography>

              <TextField
                fullWidth
                type="number"
                label="Timeout (minutes)"
                value={settings.unpaid_timeout_minutes}
                onChange={(e) => handleFieldChange('unpaid_timeout_minutes', e.target.value)}
                onBlur={() => handleFieldBlur('unpaid_timeout_minutes')}
                error={!!errors.unpaid_timeout_minutes}
                helperText={errors.unpaid_timeout_minutes || 'Range: 0-10080 minutes (0 = never expire, up to 7 days)'}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <ScheduleIcon />
                    </InputAdornment>
                  ),
                  endAdornment: settings.unpaid_timeout_minutes !== originalSettings?.unpaid_timeout_minutes && (
                    <InputAdornment position="end">
                      <Chip label="Modified" size="small" color="warning" />
                    </InputAdornment>
                  ),
                }}
                inputProps={{
                  min: 0,
                  max: 10080,
                  step: 1,
                }}
              />

              <Box sx={{ mt: 1, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                {settings.unpaid_timeout_minutes && (
                  <Chip
                    label={`≈ ${minutesToHoursDisplay(settings.unpaid_timeout_minutes)}`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                )}
                {metadata?.defaults && (
                  <Typography variant="caption" color="text.secondary">
                    Default: {metadata.defaults.unpaid_timeout_minutes} min ({minutesToHoursDisplay(metadata.defaults.unpaid_timeout_minutes)})
                  </Typography>
                )}
              </Box>
            </Grid>
          </Grid>

          {/* Info Alert */}
          <Alert severity="info" icon={<InfoIcon />} sx={{ mt: 3 }}>
            <AlertTitle>How it works</AlertTitle>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li>
                <strong>Paid bookings:</strong> After a customer pays, the host has the specified
                time to approve or reject the booking. If no action is taken, the booking is
                automatically cancelled and the customer is refunded.
              </li>
              <li>
                <strong>Admin/partner bookings:</strong> the customer is asked to pay (in the app or by the
                payment link). Unpaid ones expire after this window and the customer is told. Bookings made in
                the app are paid at checkout and aren't affected.
              </li>
            </ul>
          </Alert>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          startIcon={<ResetIcon />}
          onClick={handleReset}
          disabled={saving || rateLimitRetryAfter !== null}
        >
          Reset to Defaults
        </Button>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving || !hasChanges || rateLimitRetryAfter !== null}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </Box>

      {/* Conflict Resolution Dialog */}
      <Dialog open={showConflictDialog} onClose={() => setShowConflictDialog(false)}>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ErrorIcon color="error" />
            Settings Conflict
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            These settings were updated by another administrator while you were making changes.
            Please refresh the page to see the latest values before making your changes.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConflictDialog(false)} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleRefreshAfterConflict} variant="contained" autoFocus>
            Refresh Now
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BookingTimeoutSettingsPage;
